/*
  Tapering schedule editor + AI detector. Lives on the Rx detail page.

  - When the prescription has no tapering_schedule, shows a banner with an
    "Auto-detect from dosage text" button (AI-powered).
  - When detection returns a schedule, renders it as an editable table the
    user confirms before saving.
  - When a schedule is present, shows the bands read-only with an "Edit" toggle.
*/
import { useState } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Sparkles, Pencil, Trash2, Plus, Save, X, AlertTriangle } from "lucide-react";
import { rx as rxApi } from "../lib/api";

function BandRow({ band, onChange, onRemove, readOnly }) {
  if (readOnly) {
    return (
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm py-1.5">
        <span className="font-mono text-[color:var(--text-muted)]">
          Days {band.day_from}–{band.day_to}
        </span>
        <span className="font-semibold">{band.dose_amount}</span>
      </div>
    );
  }
  const inputCls = "w-full px-2 py-1.5 rounded-md border text-sm min-w-0";
  const inputStyle = { borderColor: "var(--border-subtle)", background: "var(--surface)" };
  return (
    <div
      className="rounded-lg border p-2.5 sm:p-0 sm:border-0 sm:bg-transparent bg-[color:var(--surface)]"
      style={{ borderColor: "var(--border-subtle)" }}
    >
      {/* Mobile: day inputs + trash on row 1, dose input full-width row 2 */}
      <div className="sm:hidden space-y-2">
        <div className="flex items-end gap-2">
          <label className="flex-1 text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>
            From
            <input
              type="number" min="1" value={band.day_from}
              onChange={(e) => onChange({ ...band, day_from: parseInt(e.target.value || "1", 10) })}
              className={`mt-1 ${inputCls} font-mono`}
              style={inputStyle}
            />
          </label>
          <label className="flex-1 text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>
            To
            <input
              type="number" min="1" value={band.day_to}
              onChange={(e) => onChange({ ...band, day_to: parseInt(e.target.value || "1", 10) })}
              className={`mt-1 ${inputCls} font-mono`}
              style={inputStyle}
            />
          </label>
          <button
            type="button" onClick={onRemove} aria-label="Remove band"
            className="h-9 w-9 shrink-0 rounded-md hover:bg-[color:var(--surface-2)] flex items-center justify-center border"
            style={{ borderColor: "var(--border-subtle)" }}
          >
            <Trash2 className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
          </button>
        </div>
        <input
          type="text" value={band.dose_amount}
          onChange={(e) => onChange({ ...band, dose_amount: e.target.value })}
          placeholder="e.g. 8 tablets"
          className={inputCls}
          style={inputStyle}
          aria-label="Dose amount"
        />
      </div>
      {/* Desktop (sm+): single-row grid */}
      <div className="hidden sm:grid grid-cols-[80px_80px_1fr_auto] gap-2 items-center">
        <input
          type="number" min="1" value={band.day_from}
          onChange={(e) => onChange({ ...band, day_from: parseInt(e.target.value || "1", 10) })}
          className={`${inputCls} font-mono`} style={inputStyle} aria-label="Day from"
        />
        <input
          type="number" min="1" value={band.day_to}
          onChange={(e) => onChange({ ...band, day_to: parseInt(e.target.value || "1", 10) })}
          className={`${inputCls} font-mono`} style={inputStyle} aria-label="Day to"
        />
        <input
          type="text" value={band.dose_amount}
          onChange={(e) => onChange({ ...band, dose_amount: e.target.value })}
          placeholder="e.g. 8 tablets"
          className={inputCls} style={inputStyle} aria-label="Dose amount"
        />
        <button
          type="button" onClick={onRemove} aria-label="Remove band"
          className="h-8 w-8 rounded-md hover:bg-[color:var(--surface-2)] flex items-center justify-center"
        >
          <Trash2 className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
        </button>
      </div>
    </div>
  );
}

export default function TaperingPanel({ rx: rxData, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const current = rxData?.tapering_schedule || null;

  function startEdit() {
    setDraft(
      (current && current.length > 0)
        ? current.map((b) => ({ ...b }))
        : [{ day_from: 1, day_to: 7, dose_amount: "" }]
    );
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft(null);
  }

  async function autoDetect() {
    setDetecting(true);
    try {
      const r = await rxApi.detectTapering(rxData.id);
      const bands = r?.tapering_schedule;
      if (!bands || bands.length === 0) {
        toast.message("No tapering pattern detected in the dosage text.");
        return;
      }
      setDraft(bands.map((b) => ({ ...b })));
      setEditing(true);
      toast.success(`Detected ${bands.length} band${bands.length === 1 ? "" : "s"} — review and save.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Detection failed");
    } finally {
      setDetecting(false);
    }
  }

  function validate(bands) {
    if (!bands?.length) return "Add at least one band.";
    const sorted = [...bands].sort((a, b) => a.day_from - b.day_from);
    for (const b of sorted) {
      if (!b.dose_amount?.trim()) return "Every band needs a dose amount.";
      if (!(b.day_from >= 1 && b.day_to >= b.day_from)) return "Each band's day_to must be ≥ day_from ≥ 1.";
    }
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].day_from <= sorted[i - 1].day_to) return "Bands must not overlap.";
    }
    return null;
  }

  async function save() {
    const err = validate(draft);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        tapering_schedule: draft.map((b) => ({
          day_from: Number(b.day_from),
          day_to: Number(b.day_to),
          dose_amount: b.dose_amount.trim(),
        })),
      };
      const updated = await rxApi.update(rxData.id, payload);
      toast.success("Tapering schedule saved");
      setEditing(false);
      setDraft(null);
      if (onUpdated) onUpdated(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function clearAll() {
    if (!window.confirm("Remove tapering schedule? Each dose will fall back to the raw dosage text.")) return;
    setSaving(true);
    try {
      const updated = await rxApi.update(rxData.id, { tapering_schedule: [] });
      toast.success("Tapering cleared");
      if (onUpdated) onUpdated(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setSaving(false);
    }
  }

  if (!rxData) return null;

  return (
    <section className="card-tactile rounded-2xl p-5 mb-6" data-testid="tapering-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4 w-4 shrink-0" style={{ color: "var(--primary-ink)" }} />
            <h3 className="font-display font-bold text-lg tracking-tight">Tapering schedule</h3>
            {current?.length > 0 && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}
                data-testid="tapering-active-badge"
              >
                Active
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
            Shows the correct tablet count for each day instead of just the raw label.
          </p>
        </div>
        {!editing && (
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:shrink-0">
            {!current?.length && (
              <Button
                size="sm" variant="outline" onClick={autoDetect} disabled={detecting}
                data-testid="tapering-auto-detect"
                className="border-[color:var(--border-subtle)] flex-1 sm:flex-none"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {detecting ? "Detecting…" : "Auto-detect"}
              </Button>
            )}
            <Button
              size="sm" variant="outline" onClick={startEdit}
              data-testid="tapering-edit"
              className="border-[color:var(--border-subtle)] flex-1 sm:flex-none"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              {current?.length ? "Edit" : "Add manually"}
            </Button>
          </div>
        )}
      </div>

      {!editing && !current?.length && (
        <div
          data-testid="tapering-empty"
          className="text-sm rounded-lg p-3 flex items-start gap-2"
          style={{
            background: "color-mix(in srgb, var(--warning) 10%, transparent)",
            color: "var(--text-main)",
          }}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "var(--warning)" }} />
          <div>
            <div className="font-semibold">No tapering set</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
              Each dose reminder will show "{rxData.dosage}" — which is unsafe for tapering regimens.
              Click <b>Auto-detect</b> if your dosage text describes a step-down, or add bands manually.
            </div>
          </div>
        </div>
      )}

      {!editing && current?.length > 0 && (
        <div className="divide-y divide-[color:var(--border-subtle)]" data-testid="tapering-bands-readonly">
          {current.map((b, i) => <BandRow key={i} band={b} readOnly />)}
          <div className="pt-3 flex justify-end">
            <button
              type="button" onClick={clearAll} disabled={saving}
              data-testid="tapering-clear"
              className="text-xs font-semibold hover:underline disabled:opacity-50"
              style={{ color: "var(--text-muted)" }}
            >
              Remove schedule
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-2" data-testid="tapering-editor">
          <div className="hidden sm:grid grid-cols-[80px_80px_1fr_auto] gap-2 text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>
            <span>Day from</span>
            <span>Day to</span>
            <span>Dose</span>
            <span />
          </div>
          {draft.map((b, i) => (
            <BandRow
              key={i}
              band={b}
              onChange={(nb) => setDraft((prev) => prev.map((x, idx) => (idx === i ? nb : x)))}
              onRemove={() => setDraft((prev) => prev.filter((_, idx) => idx !== i))}
            />
          ))}
          <button
            type="button"
            onClick={() => setDraft((prev) => {
              const last = prev[prev.length - 1];
              const nextFrom = last ? last.day_to + 1 : 1;
              return [...prev, { day_from: nextFrom, day_to: nextFrom + 6, dose_amount: "" }];
            })}
            data-testid="tapering-add-band"
            className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
            style={{ color: "var(--primary-ink)" }}
          >
            <Plus className="h-3 w-3" /> Add band
          </button>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={cancelEdit} data-testid="tapering-cancel"
              className="border-[color:var(--border-subtle)]">
              <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="tapering-save"
              className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white">
              <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
